'use client'
import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  type ShoppingStoresResponse,
  type ShoppingStore,
  type ShoppingStoreParams,
} from '@/features/shop/services/ShoppingStore/types'
import { useGuestParams } from '@/features/account/services/GuestUser/hooks'
import { DELIVERY_TYPES } from '@/features/shared/constants/global'
import {
  getIsGuestUser,
  getIsZipOnlySignedInUser,
  getUserAddressParams,
  getUserZip,
} from '@/features/account/services/User/utils'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { getAddresses } from '@/features/account/services/Addresses/utils'
import { MembershipPlanType } from '@/features/account/services/MembershipPlan/types'
import {
  useQueryShoppingStoreById,
  useQueryShoppingStores,
} from '@/features/shop/services/ShoppingStore/queries'
import { useQueryMembershipPlan } from '@/features/account/services/MembershipPlan/queries'
import { INFERRED_ZIP } from '@shared/constants/zip'
import { getCookie } from '@/features/shared/utils/cookies'
import { getAnonymousId } from '@shipt/analytics-member-web'
import { userQueryOptions } from '@/features/account/services/User/queryOptions'
import { createPassiveQueryStore } from '@/features/shared/utils/dataFetching/createPassiveQueryStore'
import { useZip } from '@/features/account/services/Zip/hooks'
import { useZipUserStore } from '@/features/shop/services/ShoppingStore/hooks'

export const useUser = createPassiveQueryStore(() => userQueryOptions)
/**
 * Used by GraphQL queries that require a customer ID to be passed in an input parameter. This looks for the logged-in user ID; else, it falls back to the anonymous ID.
 * Note that this function will return undefined if the user session is still loading or if there's no anonymous ID for the user. Queries that depend on this hook should only enable the query once they receive a value as we should always have a value if running in the browser
 **/
export const useUserIdOrAnonId = () => {
  const { userId, isSessionLoading } = useSession()
  if (isSessionLoading) {
    return undefined
  }
  const anonId: string | undefined = getAnonymousId()
  return userId || anonId
}

export const useIsUserZipOnlySignedInUser = (): boolean => {
  const user = useUser()
  return getIsZipOnlySignedInUser(user)
}

export const useZipForZipOnlySignedInUser = (): string | undefined => {
  const zipOnlyUser = useIsUserZipOnlySignedInUser()
  const zip = useZip()
  return zipOnlyUser ? zip : undefined
}

export const useIsUserC360 = () => useUser()?.subscription?.type === MembershipPlanType.CIRCLE_360

export const useUserId = () => useUser()?.id || null

export const useUserDefaultShoppingAddressId = (): number =>
  useUser()?.default_shopping_address_id ?? 0

export const useIsPickup = (): boolean => {
  const user = useUser()
  const { guestStore } = useGuestParams() ?? {}
  const storeCookie = useZipUserStore()
  return (
    user?.order_delivery_type === DELIVERY_TYPES.PICKUP ||
    Boolean(guestStore?.is_pickup) ||
    Boolean(storeCookie?.is_pickup)
  )
}

export const useIsUserExempt = (): boolean => {
  // this hook drives the exempt experience, but Circle360 now takes priority
  const isCircle360 = useIsUserC360()
  return Boolean(useUser()?.exempt) && !isCircle360
}

export const useUserStoreLocationId = (): number => {
  const user = useUser()
  const { guestStore } = useGuestParams() ?? {}
  const storeCookie = useZipUserStore()
  return (
    user?.store_location_id || guestStore?.store_location_id || storeCookie?.store_location_id || 0
  )
}

export const useStoreId = (): number => {
  const userStoreId = useUser()?.store_id ?? 0
  const { guestStore } = useGuestParams() ?? {}
  const storeCookie = useZipUserStore()
  return userStoreId || guestStore?.id || storeCookie?.id || 0
}

/** @deprecated Use useZip() instead. */
export const useUserZip = (): string => {
  const user = useUser()
  const { guestAddress } = useGuestParams() ?? {}
  if (getIsZipOnlySignedInUser(user)) {
    return getCookie(INFERRED_ZIP)
  }
  return user ? getUserZip(user) : (guestAddress?.zip_code ?? '')
}

export const useUserStore = (): ShoppingStore | null => {
  const user = useUser()
  const storeCookie = useZipUserStore()
  const zipOnlyUserShoppingStore = useQueryShoppingStoreById(
    storeCookie?.id ?? 0,
    !!storeCookie?.id,
  )
  if (getIsZipOnlySignedInUser(user)) {
    return zipOnlyUserShoppingStore.data || null
  }
  return user?.store ?? null
}

export const useUserStoreName = (): string => {
  const userStore = useUserStore()
  const { guestStore } = useGuestParams() ?? {}
  return userStore?.name || guestStore?.name || ''
}

export const useUserStoreImage = (): {
  image: string
  backgroundColor: string
} => {
  const {
    background_color: userBackgroundColor,
    circular_image_url: userCircularImage,
    image: userImage,
  } = useUserStore() ?? {}
  const storeLocationId = useUserStoreLocationId()
  const userStoreImage = userCircularImage ?? userImage
  const guestStoreQuery = !userStoreImage && !!storeLocationId

  const selectFn = useCallback(
    (data: ShoppingStoresResponse) => {
      const { delivery_stores, pickup_stores } = data

      if (!storeLocationId) return undefined

      return [...delivery_stores, ...pickup_stores].find(
        (store) => store.store_location_id === storeLocationId,
      )
    },
    [storeLocationId],
  )

  const { data } = useQueryShoppingStores({
    selectFn,
    isDisabled: !guestStoreQuery,
  })
  const guestStoreImage = data?.circular_image || data?.image
  const guestBackgroundColor = data?.background_color

  return useMemo(
    () => ({
      image: userStoreImage ?? guestStoreImage ?? '',
      backgroundColor: userBackgroundColor ?? guestBackgroundColor ?? '',
    }),
    [userStoreImage, guestStoreImage, userBackgroundColor, guestBackgroundColor],
  )
}

export const useIsMember = () => {
  const isExempt = useUser()?.exempt
  const id = useQueryMembershipPlan().data?.plan?.id
  return Boolean(isExempt || id)
}

export const useIsGuestUser = () => {
  const { guestAddress } = useGuestParams() ?? {}
  const { isSessionValid, sessionData } = useSession()

  return useMemo(
    () => getIsGuestUser({ isSessionValid, sessionData, guestAddress }),
    [guestAddress, isSessionValid, sessionData],
  )
}

/**
 * Gets whether the user is a visitor or a guest user with only a zip.
 * A user is a visitor if they are not logged in and have no guest
 * address.
 */
export const useIsVisitorOrZipOnly = (): boolean => {
  const { userId } = useSession()
  const { guestAddress } = useGuestParams() ?? {}
  return !userId && !guestAddress?.street1
}

export const useUserAddressParams = ({ zip = '' } = {}): ShoppingStoreParams => {
  const isGuestUser = useIsGuestUser()
  const queryClient = useQueryClient()
  const addressId = useUserDefaultShoppingAddressId() || getAddresses(queryClient)?.[0]?.id
  const { guestAddress } = useGuestParams() ?? {}
  const zipForZipOnlyUser = useZipForZipOnlySignedInUser()

  return getUserAddressParams({ zip, zipForZipOnlyUser, isGuestUser, addressId, guestAddress })
}
